
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Share2, Sparkles } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { motion } from "framer-motion";

type ListingRow = Database["public"]["Tables"]["listings"]["Row"];
interface Listing extends Omit<ListingRow, 'images'> {
  images: string[];
}

interface ListingsGridProps {
  listings: Listing[];
  onListingClick: (listingId: string) => void;
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export const ListingsGrid = ({ listings, onListingClick }: ListingsGridProps) => {
  return (
    <motion.div 
      className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6"
      variants={container}
      initial="hidden"
      animate="show"
    >
      {listings.map((listing, index) => (
        <motion.div
          key={listing.id}
          variants={item}
          whileHover={{ y: -5, scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          <Card 
            className="group relative bg-gradient-to-br from-white to-blue-50/50 backdrop-blur-sm overflow-hidden border-blue-100/50 hover:border-blue-200/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-100/30"
            onClick={() => onListingClick(listing.id)}
            data-listing-id={listing.id}
          >
            {listing.images && listing.images.length > 0 && (
              <div className="aspect-video relative overflow-hidden rounded-t-lg">
                <img
                  src={listing.images[0]}
                  alt={listing.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-3 left-3 right-3 flex justify-between items-center">
                    <span className="text-white text-xs sm:text-sm font-medium drop-shadow-lg flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" /> View Details
                    </span>
                    <Share2 className="w-3 h-3 sm:w-4 sm:h-4 text-white drop-shadow-lg" />
                  </div>
                </div>
              </div>
            )}
            <CardHeader className="p-3 sm:p-4">
              <CardTitle className="text-sm sm:text-base font-semibold text-gray-800 group-hover:text-blue-600 transition-colors leading-tight mb-1">
                {listing.title}
              </CardTitle>
              <CardDescription className="text-[10px] sm:text-xs text-blue-500/70 font-medium">
                {listing.business_type}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-3 pb-3 sm:px-4 sm:pb-4 pt-0">
              <p className="text-[11px] sm:text-xs text-gray-500 line-clamp-2 mb-3 leading-normal group-hover:text-gray-600 transition-colors">
                {listing.description}
              </p>
              {listing.price && (
                <div className="relative overflow-hidden">
                  <motion.p 
                    className="text-sm sm:text-base font-bold text-emerald-600"
                    whileHover={{ scale: 1.05 }}
                  >
                    ₱{listing.price.toLocaleString()}
                  </motion.p>
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-100/20 to-green-100/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </motion.div>
  );
};
